import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sparkles, Trash2, Play, Zap, TrendingUp, Lock } from "lucide-react";

const SUBTYPE_STYLES = {
  action: { bg: "from-blue-500 to-indigo-600", text: "text-white", labelKey: "effectCard.subtype.action" },
  event: { bg: "from-orange-500 to-orange-700", text: "text-white", labelKey: "effectCard.subtype.event" },
  market_melee: { bg: "from-red-500 to-rose-600", text: "text-white", labelKey: "effectCard.subtype.marketMelee" },
};

export default function EffectCard({ card, playable, discardable, onPlay, onDiscard, onInspect }) {
  const { t } = useTranslation();
  // Hidden / face-down variant for opponent hands.
  if (card?.hidden) {
    return (
      <div
        data-testid={`effect-card-hidden-${card.id}`}
        className="effect-card relative flex-shrink-0 w-28 sm:w-36 rounded-2xl border-2 border-[#1A237E]/30 dark:border-[#E3F2FD]/30 overflow-hidden bg-gradient-to-br from-[#1A237E] to-[#38B6FF] shadow-[inset_0_0_24px_rgba(255,255,255,0.08)] h-[6rem] sm:h-[7.5rem]"
      >
        <div className="h-1.5 bg-[#8247E5]" />
        <div className="flex flex-col items-center justify-center h-full text-white/90 gap-1">
          <Lock className="w-5 h-5 opacity-60" />
          <p
            className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-70"
            style={{ fontFamily: "Fredoka" }}
          >
            {t("effectCard.label.hidden")}
          </p>
        </div>
      </div>
    );
  }

  const style = SUBTYPE_STYLES[card.subtype] || SUBTYPE_STYLES.action;
  const isPersistentModifier = !!card.persistent_symbol;
  const borderClass = isPersistentModifier
    ? "border-purple-400"
    : "border-black/10 dark:border-white/15";
  const bgClass = isPersistentModifier
    ? "bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/40 dark:to-indigo-900/40"
    : "bg-white dark:bg-slate-800";
  const canInspect = !isPersistentModifier && playable;

  return (
    <div
      data-testid={`effect-card-${card.id}`}
      role={canInspect ? "button" : undefined}
      tabIndex={canInspect ? 0 : undefined}
      onClick={canInspect && onInspect ? () => onInspect(card) : undefined}
      onKeyDown={
        canInspect && onInspect
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onInspect(card);
              }
            }
          : undefined
      }
      className={`effect-card relative flex-shrink-0 w-28 sm:w-36 rounded-2xl border-2 ${borderClass} ${bgClass} overflow-hidden ${
        canInspect
          ? "cursor-pointer hover:border-[#38B6FF] hover:shadow-md hover:-translate-y-0.5 transition-all"
          : ""
      }`}
    >
      {/* Header */}
      <div className={`bg-gradient-to-r ${style.bg} px-2 py-1.5 flex items-center gap-1`}>
        {card.subtype === "market_melee" ? <Zap className="w-3 h-3 text-white" /> : <Sparkles className="w-3 h-3 text-white" />}
        <span className="text-[10px] font-bold text-white uppercase tracking-wider">{t(style.labelKey)}</span>
        {isPersistentModifier && (
          <span className="ml-auto text-[9px] font-bold text-white/90 bg-white/20 dark:bg-slate-900/20 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
            <TrendingUp className="w-2.5 h-2.5" /> {t("effectCard.label.active")}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-2">
        <p className="text-xs font-bold text-[#1A237E] dark:text-[#E3F2FD] leading-tight mb-1" style={{ fontFamily: 'Fredoka' }}>
          {card.name}
        </p>
        {isPersistentModifier ? (
          <div className="bg-purple-100 rounded-md px-1.5 py-0.5 inline-flex items-center gap-1 mb-1">
            <span className="text-[9px] font-bold text-purple-700">
              {t("effectCard.persistentBonus", { amount: card.persistent_amount, symbol: card.persistent_symbol })}
            </span>
          </div>
        ) : card.resolutionType && (
          <span className={`text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded ${
            card.resolutionType === "holdable" ? "bg-blue-100 text-blue-600" :
            card.resolutionType === "persistent" ? "bg-purple-100 text-purple-600" :
            "bg-gray-100 text-gray-600"
          }`}>
            {card.resolutionType}
          </span>
        )}
        <p className="text-[10px] text-[#1A237E]/60 dark:text-[#E3F2FD]/60 leading-tight line-clamp-3 mt-1">
          {card.description}
        </p>
        {isPersistentModifier && (
          <p className="text-[9px] text-purple-600/80 italic mt-1">
            {t("effectCard.persistentClearHint", { symbol: card.persistent_symbol })}
          </p>
        )}
      </div>

      {/* Action buttons — never shown for persistent modifier cards */}
      {!isPersistentModifier && (playable || discardable) && (
        <div className="px-2 pb-2 flex gap-1">
          {playable && (
            <Button
              data-testid={`play-card-${card.id}`}
              size="sm"
              className="flex-1 h-6 text-[10px] bg-[#38B6FF] hover:bg-[#2196F3] text-white rounded-lg"
              onClick={(e) => { e.stopPropagation(); onPlay(); }}
            >
              <Play className="w-3 h-3 mr-0.5" /> {t("effectCard.button.play")}
            </Button>
          )}
          {discardable && (
            <Button
              data-testid={`discard-card-${card.id}`}
              size="sm"
              variant="destructive"
              className="flex-1 h-6 text-[10px] rounded-lg"
              onClick={(e) => { e.stopPropagation(); onDiscard(); }}
            >
              <Trash2 className="w-3 h-3 mr-0.5" /> {t("effectCard.button.discard")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
